import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Heart, Sparkles, Building2, ShieldCheck, ArrowRight, ArrowLeft, Users, Star, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const AMOUNTS = [0, 100, 400];

const AmountPicker = ({ value, onChange, customKey, custom, testPrefix }) => (
  <div className="flex flex-wrap items-center gap-2">
    {AMOUNTS.map((amt) => (
      <button
        type="button"
        key={amt}
        onClick={() => onChange(amt)}
        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
          value === amt
            ? "bg-[#EC008C] text-black"
            : "bg-[#050810] border border-[#1A2030] text-[#C0C6D0] hover:border-[#EC008C]/50"
        }`}
        data-testid={`${testPrefix}-${amt}`}
      >
        {amt === 0 ? "Skip" : `$${amt}`}
      </button>
    ))}
    <button
      type="button"
      onClick={() => onChange(-1)}
      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
        value === -1
          ? "bg-[#EC008C] text-black"
          : "bg-[#050810] border border-[#1A2030] text-[#C0C6D0] hover:border-[#EC008C]/50"
      }`}
      data-testid={`${testPrefix}-custom`}
    >
      Custom
    </button>
    {value === -1 && (
      <div className="flex items-center gap-1">
        <span className="text-[#8A92A0]">$</span>
        <Input
          type="number"
          min="1"
          value={custom}
          onChange={(e) => customKey(e.target.value)}
          placeholder="Amount"
          className="bg-[#050810] border-[#1A2030] text-white w-28"
          data-testid={`${testPrefix}-custom-input`}
        />
      </div>
    )}
  </div>
);

/**
 * Sponsors — Contribution & Shoutout Form.
 * A single friendly form for a business/brand to:
 *   1) Tip a favourite artist (on-platform preferred, else invite / platform-routed)
 *   2) Tip the platform to keep the movement growing
 * Their company info is posted to the Community feed as a public shoutout.
 * Payments go LIVE with 88/12 split for artist tips (90/10 for Founders Circle).
 */
const SponsorContribute = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    sponsor_name: "",
    company_name: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    artist_name: "",
    send_to_preferred: false,
    tip_artist: 0,
    custom_artist: "",
    tip_platform: 0,
    custom_platform: "",
    consent: true,
    shoutout_note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const artistAmount = form.tip_artist === -1 ? Math.max(0, Math.floor(Number(form.custom_artist) || 0)) : form.tip_artist;
  const platformAmount = form.tip_platform === -1 ? Math.max(0, Math.floor(Number(form.custom_platform) || 0)) : form.tip_platform;
  const totalUsd = artistAmount + platformAmount;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.sponsor_name.trim() || !form.company_name.trim() || !form.contact_email.trim()) {
      toast.error("Name, company, and contact email are required.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm you're OK with the community shoutout.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sponsors/contribute`, {
        sponsor_name: form.sponsor_name.trim(),
        company_name: form.company_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim() || null,
        website: form.website.trim() || null,
        artist_name: form.artist_name.trim() || null,
        send_to_preferred: form.send_to_preferred,
        tip_artist_cents: Math.round(artistAmount * 100),
        tip_platform_cents: Math.round(platformAmount * 100),
        shoutout_note: form.shoutout_note.trim() || null,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      // No payment — free shoutout registered
      toast.success("Thank you! Your shoutout is live in the community feed.");
      navigate("/community");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="sponsor-contribute-page">
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/70 backdrop-blur-lg border-b border-[#1A2030]">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#8A92A0] hover:text-white text-sm" data-testid="sponsor-back-btn">
            <ArrowLeft className="w-4 h-4" /> StardomFans
          </Link>
          <div className="flex items-center gap-2 text-xs text-[#8A92A0]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#EC008C]" />
            <span className="hidden sm:inline">Secure LIVE checkout · 90% goes to the artist</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#EC008C]/15 flex items-center justify-center mb-4">
              <Heart className="w-7 h-7 text-[#EC008C]" />
            </div>
            <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-white mb-3">
              Sponsors · Support the Movement
            </h1>
            <p className="text-[#8A92A0] leading-relaxed max-w-lg mx-auto">
              Tip an independent artist. Tip the platform. Your company name and contact info will be posted to the <span className="text-white font-semibold">Community Feed</span> as a public shoutout for anyone who needs your service.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-testid="sponsor-form">
            {/* Contact block */}
            <section className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-[#EC008C]" />
                <h2 className="text-white font-semibold text-lg">Your business</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} placeholder="Your name" required className="bg-[#050810] border-[#1A2030] text-white" data-testid="sponsor-name" />
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company / brand" required className="bg-[#050810] border-[#1A2030] text-white" data-testid="sponsor-company" />
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact email" required className="bg-[#050810] border-[#1A2030] text-white" data-testid="sponsor-email" />
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Phone (optional)" className="bg-[#050810] border-[#1A2030] text-white" data-testid="sponsor-phone" />
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="Website (optional)" className="bg-[#050810] border-[#1A2030] text-white sm:col-span-2" data-testid="sponsor-website" />
              </div>
            </section>

            {/* Tip an artist */}
            <section className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#EC008C]" />
                <h2 className="text-white font-semibold text-lg">Tip an artist</h2>
              </div>
              <p className="text-xs text-[#8A92A0] mb-3">
                If you don&apos;t see your artist on the platform, we&apos;ll help invite them — or route the tip to a preferred verified artist so it still supports the Movement.
              </p>
              <Input
                value={form.artist_name}
                onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                placeholder="Artist name"
                className="bg-[#050810] border-[#1A2030] text-white mb-3"
                data-testid="sponsor-artist-name"
              />
              <label className="flex items-center gap-2 text-xs text-[#8A92A0] mb-4 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_to_preferred}
                  onChange={(e) => setForm({ ...form, send_to_preferred: e.target.checked })}
                  className="w-4 h-4 accent-[#EC008C]"
                  data-testid="sponsor-preferred-checkbox"
                />
                If my artist isn&apos;t on the platform yet, route my tip to a preferred verified artist.
              </label>
              <div className="pt-3 border-t border-[#1A2030]">
                <p className="text-xs text-[#8A92A0] mb-2">Your choice:</p>
                <AmountPicker
                  value={form.tip_artist}
                  onChange={(v) => setForm({ ...form, tip_artist: v })}
                  custom={form.custom_artist}
                  customKey={(v) => setForm({ ...form, custom_artist: v })}
                  testPrefix="sponsor-tip-artist"
                />
              </div>
            </section>

            {/* Tip the platform */}
            <section className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#EC008C]" />
                <h2 className="text-white font-semibold text-lg">Tip the platform</h2>
              </div>
              <p className="text-xs text-[#8A92A0] mb-3">
                Help us keep StardomFans.com running independently — server costs, moderation, and the Top 100 program.
              </p>
              <p className="text-xs text-[#8A92A0] mb-2">Your choice:</p>
              <AmountPicker
                value={form.tip_platform}
                onChange={(v) => setForm({ ...form, tip_platform: v })}
                custom={form.custom_platform}
                customKey={(v) => setForm({ ...form, custom_platform: v })}
                testPrefix="sponsor-tip-platform"
              />
            </section>

            {/* Community shoutout */}
            <section className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-[#EC008C]" />
                <h2 className="text-white font-semibold text-lg">Community Shoutout</h2>
              </div>
              <p className="text-xs text-[#8A92A0] mb-3">
                Your company name and contact info will be featured in the <span className="text-white font-semibold">Community Feed</span> for anyone who needs your service.
              </p>
              <Textarea
                value={form.shoutout_note}
                onChange={(e) => setForm({ ...form, shoutout_note: e.target.value })}
                placeholder="Optional — a short line about what your business does (max 240 chars)"
                maxLength={240}
                rows={2}
                className="bg-[#050810] border-[#1A2030] text-white resize-none"
                data-testid="sponsor-shoutout-note"
              />
              <label className="flex items-start gap-2 mt-3 text-xs text-[#8A92A0] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="w-4 h-4 mt-0.5 accent-[#EC008C]"
                  data-testid="sponsor-consent"
                />
                I&apos;m happy for my company name, contact info, and shoutout note to appear as a public post in the Community Feed. Personal info like phone stays private.
              </label>
            </section>

            {/* Total + Submit */}
            <div className="card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wide text-[#8A92A0]">Your total contribution</p>
                <p className="font-['Unbounded'] text-3xl font-bold text-white" data-testid="sponsor-total">
                  ${totalUsd.toFixed(2)}
                </p>
                <p className="text-xs text-[#8A92A0] mt-1">
                  {totalUsd === 0
                    ? "Zero contribution — you'll still get a free shoutout in the community feed."
                    : `Artist: $${artistAmount} · Platform: $${platformAmount} · 90% goes to the artist.`}
                </p>
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full px-8 py-6 text-base font-semibold"
                data-testid="sponsor-submit-btn"
              >
                {submitting ? "Processing..." : (totalUsd > 0 ? <>Continue to Payment <ArrowRight className="w-4 h-4 ml-1.5" /></> : <><Send className="w-4 h-4 mr-1.5" /> Post Shoutout</>)}
              </Button>
            </div>

            <p className="text-center text-xs text-[#8A92A0]">
              Secure LIVE checkout via our PCI-DSS Level 1 processor. Your card details never touch our servers. Non-hackable by design.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SponsorContribute;
